import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { RefObject, useCallback, useState } from 'react';
import { Checkbox } from '@components/ui/checkbox';
import { Option } from './types';
import { cn } from '@utils/cn';
import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';

export interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  placeholder?: string;
  error?: FieldError;
  disabled?: boolean;
  limit?: number;
  singleMode?: boolean;
  popoverRef?: RefObject<HTMLElement>;
}

const VISIBLE_FILTERS_LIMIT = 2;

export const MultiSelect = ({
  options,
  value,
  onChange,
  className,
  placeholder,
  error,
  disabled,
  limit = VISIBLE_FILTERS_LIMIT,
  singleMode,
  popoverRef,
}: MultiSelectProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms.default.multiSelect' });
  const [open, setOpen] = useState(false);
  const optionsMap = Object.fromEntries(options.map((el) => [el.value, el.label]));

  const isOptionChecked = useCallback((v: string) => value.includes(v), [value]);

  const setOptionChecked = (v: string) => {
    if (isOptionChecked(v)) onChange(value.filter((el) => el !== v));
    else onChange(singleMode ? [v] : [...value, v]);
    if (singleMode) setOpen(false);
  };

  const generateChecked = () => {
    const items = [];
    for (const [index, item] of value.entries()) {
      if (index < limit) items.push(options.find((el) => el.value === item)?.label);
      else {
        items.push(`+${value.length - limit}`);
        break;
      }
    }
    return items.join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          className={cn(
            'group-[.error]:shadow-danger-b flex h-8 w-full items-center justify-between gap-2 rounded border border-neutral-300 bg-white px-3 text-m font-bold text-neutral-800 focus-within:border-primary-600 focus-within:outline focus-within:outline-2 focus-within:outline-primary-200',
            error && 'border-danger-500 focus-within:border-danger-500',
            disabled && 'border-neutral-200 bg-neutral-50 text-neutral-400',
            className,
          )}>
          {value.length ? (
            <span>{generateChecked()}</span>
          ) : (
            <span className="font-normal">{placeholder || t('select')}</span>
          )}
          <ChevronDown className="text-neutral-90 h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        container={popoverRef?.current}
        className={singleMode ? 'p-0' : 'w-[200px] p-0'}>
        <Command
          filter={(v, search) =>
            Number(optionsMap[v]?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase()))
          }>
          <CommandInput placeholder={t('search')} />
          <CommandList>
            <CommandEmpty>{t('empty')}</CommandEmpty>
            <CommandGroup className="scrollbar-thin max-h-[336px] overflow-y-auto p-0">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  className={cn([
                    'flex gap-2',
                    isOptionChecked(option.value)
                      ? 'bg-primary-500 text-white '
                      : 'hover:bg-primary-200',
                  ])}
                  value={option.value}
                  disabled={disabled}
                  onSelect={() => {
                    setOptionChecked(option.value);
                  }}>
                  {!singleMode && <Checkbox checked={isOptionChecked(option.value)} />}
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <div className="flex justify-end border-t px-2.5 py-1.5">
              <button
                type="button"
                disabled={Boolean(!value.length)}
                className="cursor-pointer text-xs font-medium text-primary-600 hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
                onClick={() => onChange([])}>
                {t('clear').toLocaleUpperCase()}
              </button>
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
